import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import ListingForm, { type ListingRecord } from '@/pages/dashboard/listings/components/ListingForm';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';

function ListingEdit() {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [listing, setListing] = useState<ListingRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!user || !id) return;
      setLoading(true);
      const { data, error: err } = await supabase
        .from('listings')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (!mounted) return;
      if (err) {
        setError(err.message);
      } else if (data) {
        setListing({
          id: data.id,
          owner_id: data.owner_id,
          type: data.type,
          title: data.title,
          description: data.description || '',
          price: data.price,
          category_id: data.category_id || '',
          city: data.city || '',
          images: data.images || [],
          files: data.files || [],
          contact_name: data.contact_name || '',
          contact_phone: data.contact_phone || '',
        });
      } else {
        setError('Annonce introuvable.');
      }
      if (mounted) setLoading(false);
    };
    load();
    return () => {
      mounted = false;
    };
  }, [user, id]);

  if (!user) return null;

  return (
    <DashboardLayout>
      <div className="mb-8">
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50">Modifier l&apos;annonce</h1>
        <p className="text-sm text-foreground-400 mt-1">
          Mettez à jour les informations de votre annonce.
        </p>
      </div>

      <div className="max-w-3xl">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
              <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" />
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-sm text-red-400 mb-4">{error}</p>
            <button
              onClick={() => navigate('/dashboard/listings')}
              className="px-5 py-2.5 border border-background-300/60 text-foreground-50 font-medium rounded-lg hover:bg-background-200/60 transition-colors text-sm whitespace-nowrap"
            >
              Retour à mes annonces
            </button>
          </div>
        ) : listing ? (
          <ListingForm
            ownerId={user.id}
            existing={listing}
            onSaved={() => navigate('/dashboard/listings')}
          />
        ) : null}
      </div>
    </DashboardLayout>
  );
}

export default ListingEdit;